import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api, { API_BASE } from '../utils/api';

interface User {
    _id: string;
    name: string;
    email: string;
    username?: string;
    phone?: string;
    title?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    about?: string;
    avatar?: string;
    facebook?: string;
    twitter?: string;
    googleplus?: string;
    linkedin?: string;
    role?: string;
    projects?: string;
    projectsSince?: string;
    projectsCompleted?: string;
    projectsOngoing?: string;
    projectsUpcoming?: string;
    permissions?: string[];
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; message?: string; user?: User }>;
    loginWithToken: (token: string) => Promise<void>;   // ✅ added
    logout: () => void;
    updateUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    token: null,
    loading: true,
    login: async () => ({ success: false, user: undefined }),
    loginWithToken: async () => {},  // ✅ added
    logout: () => {},
    updateUser: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Restore session
    useEffect(() => {
        const savedToken = localStorage.getItem('resido_token');
        const savedUser = localStorage.getItem('resido_user');

        if (savedToken && savedUser) {
            let parsedUser: User | null = null;
            try {
                parsedUser = JSON.parse(savedUser);
            } catch (e) {
                localStorage.removeItem('resido_token');
                localStorage.removeItem('resido_user');
                setLoading(false);
                return;
            }

            setToken(savedToken);
            setUser(parsedUser);

            api.getMe(savedToken)
                .then((data) => {
                    if (data && data._id) {
                        setUser(data);
                        localStorage.setItem('resido_user', JSON.stringify(data));
                    } else {
                        // Stale or expired token
                        console.warn('Session expired or invalid token:', data?.message);
                        setToken(null);
                        setUser(null);
                        localStorage.removeItem('resido_token');
                        localStorage.removeItem('resido_user');
                    }
                })
                .catch((err) => {
                    console.warn('Session validation failed:', err);
                    setToken(null);
                    setUser(null);
                    localStorage.removeItem('resido_token');
                    localStorage.removeItem('resido_user');
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    // Normal login (email/password)
    const login = async (email: string, password: string) => {
        try {
            const data = await api.login(email, password);

            if (data.token) {
                setToken(data.token);
                setUser(data.user);

                localStorage.setItem('resido_token', data.token);
                localStorage.setItem('resido_user', JSON.stringify(data.user));

                return { success: true, user: data.user };
            }

            return { success: false, message: data.message || 'Login failed' };
        } catch {
            return { success: false, message: 'Server error. Please try again.' };
        }
    };

    // 🔥 IMPORTANT: Login with token (Login As feature)
    const loginWithToken = async (newToken: string) => {
        try {
            localStorage.setItem('resido_token', newToken);

            const userData = await api.getMe(newToken);

            if (userData && userData._id) {
                setToken(newToken);
                setUser(userData);

                localStorage.setItem('resido_user', JSON.stringify(userData));
            }
        } catch (err) {
            console.error("Token login failed", err);
        }
    };

    const logout = () => {
        const savedToken = localStorage.getItem('resido_token');
        if (savedToken) {
            fetch(`${API_BASE}/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${savedToken}`,
                    'Content-Type': 'application/json'
                }
            }).catch((err) => console.error('Logout request failed:', err));
        }

        setToken(null);
        setUser(null);
        localStorage.removeItem('resido_token');
        localStorage.removeItem('resido_user');
    };

    const updateUser = (data: Partial<User>) => {
        if (user) {
            const updated = { ...user, ...data };
            setUser(updated);
            localStorage.setItem('resido_user', JSON.stringify(updated));
        }
    };

    return (
        <AuthContext.Provider
            value={{ user, token, loading, login, loginWithToken, logout, updateUser }}  // ✅ added here
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;